// DiceScene owns the single PixiJS Application and assembles the UI into five
// regions (top bar, number selector, dice area, stats, controls) over a
// full-viewport background. The Application is created ONCE; every resize /
// orientation change only re-applies positions/scales — no textures or game
// state are ever recreated, so the session, selected number, bet, balance,
// current round, and revealed dice all survive orientation changes.
//
// It implements the controller's Animator (anticipate/reveal) and drives the
// three dice through the engine-agnostic DiceRollAnimator, which it adapts onto
// DieView containers. The scene NEVER derives an outcome: reveal() is handed the
// authoritative server dice and only animates them.
import { Application, Container } from 'pixi.js';
import { classifyLayout, isMobile } from '@aerofruits/game-shell';
import type { Animator, ViewModel } from '../game/controller.js';
import { BackgroundLayer } from './background.js';
import { DieView } from './dice-face.js';
import { DiceRollAnimator, type DieFrame } from './dice-animator.js';
import { NumberCell, PillButton, StatCard, ToolButton } from './widgets.js';
import {
  computeSceneLayout,
  controlsLayout,
  diceClusterLayout,
  dprCap,
  NO_INSETS,
  statsLayout,
  toolbarSlots,
  type CompositionKind,
  type Insets,
  type LayoutMode,
  type OverlayLayoutBounds,
  type Rect,
  type SceneLayout,
  type Viewport,
} from './layout.js';
import type { ReplayPlan } from '../game/replay.js';
import { getSoundManager } from '../audio/sound-manager.js';
import { playResultSound } from '../audio/dice-sounds.js';

const BASE_DIE = 100; // DieView is drawn at this size; slots scale it to fit.
const DIE_COUNT = 3;

export interface SceneCallbacks {
  onRoll: () => void;
  onBetUp: () => void;
  onBetDown: () => void;
  onMaxBet: () => void;
  onSelectNumber: (n: number) => void;
  onHistory: () => void;
  onRules: () => void;
  onSound: () => void;
}

export interface SceneOptions {
  formatMoney: (minor: number, currency: string) => string;
  reducedMotion: () => boolean;
  callbacks: SceneCallbacks;
  /** Stable translation accessors (backed by the active I18n instance). */
  translate: {
    t: (key: string, params?: Record<string, string | number>) => string;
    tp: (baseKey: string, count: number, params?: Record<string, string | number>) => string;
  };
  /**
   * Replay-only presentation: renders ONLY the dice + result, centered, and
   * hides every live control (toolbar, number selector, stat cards, bet/roll).
   * Used by the standalone public replay page so it never shows controls that
   * cannot work without a session.
   */
  replayOnly?: boolean;
}

export class DiceScene implements Animator {
  readonly app = new Application();
  private readonly opts: SceneOptions;
  private readonly background = new BackgroundLayer();
  private readonly world = new Container();

  // Regions (positioned in screen space on every relayout).
  private readonly topbar = new Container();
  private readonly selector = new Container();
  private readonly diceArea = new Container();
  private readonly statsRow = new Container();
  private readonly controls = new Container();

  // Widgets. There is intentionally NO in-canvas "DICE" wordmark: the game is
  // already identified by the shell/lobby header (and the browser title), so an
  // internal wordmark only wastes space and competes with the toolbar/gameplay.
  private btnHistory!: ToolButton;
  private btnRules!: ToolButton;
  private btnSound!: ToolButton;
  private cells: NumberCell[] = [];
  private dieSlots: Container[] = [];
  private dice: DieView[] = [];
  private cardBalance!: StatCard;
  private cardBet!: StatCard;
  private cardWin!: StatCard;
  private btnBetDown!: PillButton;
  private btnBetUp!: PillButton;
  private btnMax!: PillButton;
  private btnRoll!: PillButton;

  private animator!: DiceRollAnimator;
  private parent: HTMLElement | undefined;
  private mode: LayoutMode = 'desktop';
  private insets: Insets = { ...NO_INSETS };
  private layout!: SceneLayout;
  private booted = false;
  private hidden = false;
  private currency = 'USD';
  private selectedNumber: number | null = null;
  private readonly replayOnly: boolean;
  // Replay is a read-only re-presentation of a stored round. While it runs we
  // ignore live view-model updates and re-apply the latest one on exit, so the
  // live state (selection, dice, balance) is never corrupted by the animation.
  private replaying = false;
  private lastVm: ViewModel | null = null;
  private replayResultBlock: Rect | null = null;

  setLayoutBoundsListener(fn: (bounds: OverlayLayoutBounds) => void): void {
    this.onLayoutBounds = fn;
    if (this.booted && (this.layout || this.replayResultBlock)) {
      this.emitLayoutBounds();
    }
  }

  private emitLayoutBounds(): void {
    if (!this.onLayoutBounds) return;
    const toScreen = (rect: Rect): Rect => {
      if (!this.layout || this.layout.kind !== 'unified') return rect;
      return {
        x: this.layout.stage.x + rect.x * this.layout.stage.scale,
        y: this.layout.stage.y + rect.y * this.layout.stage.scale,
        w: rect.w * this.layout.stage.scale,
        h: rect.h * this.layout.stage.scale,
      };
    };

    const resultSource = this.replayOnly
      ? this.replayResultBlock
      : this.layout?.regions.resultBlock;
    const reelSource = this.replayOnly ? null : this.layout?.regions.lightningReel;
    if (!resultSource) return;

    const result = toScreen(resultSource);
    const reel = reelSource ? toScreen(reelSource) : { x: 0, y: 0, w: 0, h: 0 };
    this.onLayoutBounds({
      resultBlock: {
        left: result.x,
        top: result.y,
        width: result.w,
        height: result.h,
      },
      reel: {
        left: reel.x,
        top: reel.y,
        width: reel.w,
        height: reel.h,
      },
    });
  }

  private onLayoutBounds: ((bounds: OverlayLayoutBounds) => void) | undefined;

  constructor(opts: SceneOptions) {
    this.opts = opts;
    this.replayOnly = opts.replayOnly ?? false;
  }

  async init(parent: HTMLElement, viewport: Viewport): Promise<void> {
    this.parent = parent;
    const initialMode = classifyLayout(viewport);
    await this.app.init({
      background: 0x05070f,
      antialias: !isMobile(initialMode),
      resolution: dprCap(initialMode, viewport.dpr ?? (typeof window !== 'undefined' ? window.devicePixelRatio : 1) ?? 1),
      autoDensity: true,
      width: viewport.width,
      height: viewport.height,
    });
    this.app.canvas.style.display = 'block';
    parent.appendChild(this.app.canvas);

    this.buildWidgets();

    this.world.addChild(this.topbar, this.selector, this.diceArea, this.statsRow, this.controls);
    this.app.stage.addChild(this.background, this.world);

    this.animator = new DiceRollAnimator({
      dieCount: DIE_COUNT,
      onFrame: (i, frame) => this.applyDieFrame(i, frame),
    });

    this.booted = true;
    this.relayout(viewport, this.insets);
    this.startIdle();
  }

  private buildWidgets(): void {
    this.btnHistory = new ToolButton('history', 56).onClick(() => {
      void this.soundUnlock();
      this.sound().play('ui.click');
      this.opts.callbacks.onHistory();
    });
    this.btnRules = new ToolButton('rules', 56).onClick(() => {
      void this.soundUnlock();
      this.sound().play('ui.click');
      this.opts.callbacks.onRules();
    });
    this.btnSound = new ToolButton('sound-on', 56).onClick(() => {
      void this.soundUnlock();
      this.opts.callbacks.onSound();
    });
    this.topbar.addChild(this.btnHistory, this.btnRules, this.btnSound);

    for (let n = 1; n <= 6; n++) {
      const cell = new NumberCell(n, 64).onSelect((v) => {
        void this.soundUnlock();
        this.sound().play('ui.select');
        this.opts.callbacks.onSelectNumber(v);
      });
      this.cells.push(cell);
      this.selector.addChild(cell);
    }

    for (let i = 0; i < DIE_COUNT; i++) {
      const slot = new Container();
      const die = new DieView(BASE_DIE);
      die.setBase(0, 0);
      die.setFace(1);
      slot.addChild(die);
      this.dieSlots.push(slot);
      this.dice.push(die);
      this.diceArea.addChild(slot);
    }

    this.cardBalance = new StatCard('BALANCE', 200, 80);
    this.cardBet = new StatCard('BET', 200, 80);
    this.cardWin = new StatCard('LAST WIN', 200, 80);
    this.statsRow.addChild(this.cardBalance, this.cardBet, this.cardWin);

    this.btnBetDown = new PillButton('-', 80, 64).onClick(() => this.opts.callbacks.onBetDown());
    this.btnBetUp = new PillButton('+', 80, 64).onClick(() => this.opts.callbacks.onBetUp());
    this.btnMax = new PillButton('Max', 96, 64).onClick(() => this.opts.callbacks.onMaxBet());
    this.btnRoll = new PillButton('ROLL', 220, 64, true).onClick(() => {
      void this.soundUnlock();
      this.sound().play('dice.rollPress');
      this.opts.callbacks.onRoll();
    });
    this.controls.addChild(this.btnBetDown, this.btnBetUp, this.btnMax, this.btnRoll);

    this.applyTranslations();

    if (this.replayOnly) {
      // Read-only replay: keep ONLY the dice + result on the canvas. Hide every
      // live control so it can't be seen or hit; the page's DOM panel carries
      // the round metadata and the Replay/Copy/Share actions instead.
      for (const c of [this.topbar, this.selector, this.statsRow, this.controls]) {
        c.visible = false;
        c.eventMode = 'none';
      }
    }
  }

  private startIdle(): void {
    this.app.ticker.add((ticker) => {
      if (this.opts.reducedMotion()) return;
      this.background.tickIdle(ticker.deltaMS);
    });
  }

  animatorPort(): Animator {
    return this;
  }

  setSoundOn(on: boolean): void {
    // The icon swaps (speaker vs muted) AND the button stays visually "active"
    // while ON, so the state reads at a glance on the canvas.
    this.btnSound.setIcon(on ? 'sound-on' : 'sound-off');
    this.btnSound.setActive(on);
    this.sound().setEnabled(on);
  }

  private sound() {
    return getSoundManager();
  }

  private async soundUnlock(): Promise<void> {
    await this.sound().unlock();
  }

  setHidden(hidden: boolean): void {
    if (!this.booted || hidden === this.hidden) return;
    this.hidden = hidden;
    if (hidden) this.app.ticker.stop();
    else this.app.ticker.start();
  }

  // --- Animator -----------------------------------------------------------

  /** Pre-roll: dice anticipation only. Result block is cleared by the controller. */
  anticipate(): Promise<void> {
    return Promise.resolve();
  }

  /**
   * Animates the authoritative dice and settles on the EXACT server values. The
   * animator is the only loop; it is given final values + match flags and never
   * generates randomness. Resolves when the presentation completes.
   */
  async reveal(dice: number[], matchFlags: boolean[], matchCount: number, multiplier: number): Promise<void> {
    await this.runRollAnimation(dice, matchFlags, matchCount, multiplier, this.opts.reducedMotion());
  }

  private async runRollAnimation(
    dice: number[],
    matchFlags: boolean[],
    matchCount: number,
    multiplier: number,
    reducedMotion: boolean,
  ): Promise<void> {
    const sm = this.sound();
    if (!reducedMotion) sm.startLoop('dice.rollLoop');
    try {
      await this.animator.roll(dice, matchFlags, { reducedMotion, speedScale: 1 });
    } finally {
      sm.stopLoop('dice.rollLoop');
    }
    sm.play('dice.land');
    playResultSound(matchCount, multiplier);
  }

  /** Refreshes every visible Pixi label after a locale change. */
  applyTranslations(): void {
    if (!this.booted || this.replayOnly) return;
    const { t } = this.opts.translate;
    this.cardBalance.setCaption(t('dice.stats.balance'));
    this.cardBet.setCaption(t('dice.stats.bet'));
    this.cardWin.setCaption(t('dice.stats.lastWin'));
    this.btnMax.setText(t('dice.actions.max'));
    if (this.lastVm) this.update(this.lastVm);
  }

  private applyDieFrame(i: number, frame: DieFrame): void {
    this.dice[i]?.applyFrame(frame);
  }

  // --- view model ---------------------------------------------------------

  update(vm: ViewModel): void {
    if (!this.booted) return;
    if (this.replaying) return;
    this.lastVm = vm;
    this.currency = vm.currency || this.currency;
    this.cardBalance.setValue(vm.balanceMinor === null ? '—' : this.opts.formatMoney(vm.balanceMinor, this.currency));
    this.cardBet.setValue(this.opts.formatMoney(vm.betMinor, this.currency));
    const winShown = (vm.state === 'SHOWING_WIN' || vm.state === 'READY') && vm.lastWinMinor > 0;
    this.cardWin.setValue(this.opts.formatMoney(winShown ? vm.lastWinMinor : 0, this.currency));

    if (vm.selectedNumber !== this.selectedNumber) {
      this.selectedNumber = vm.selectedNumber;
      for (const c of this.cells) c.setSelected(c.value === vm.selectedNumber);
    }

    const ready = vm.state === 'READY';
    // Roll is gated by the controller's single canRoll() truth, not merely READY:
    // it stays disabled until a number is selected (and the bet is affordable).
    this.btnRoll.setEnabled(vm.canRoll);
    const { t } = this.opts.translate;
    this.btnRoll.setText(ready ? t('dice.actions.roll') : t('dice.status.rolling'));
    this.btnBetDown.setEnabled(ready);
    this.btnBetUp.setEnabled(ready);
    this.btnMax.setEnabled(ready);
    for (const c of this.cells) c.setEnabled(ready);
  }

  /** Reveals a recovered/initial round's dice instantly (no roll animation). */
  showResult(vm: ViewModel): void {
    if (!this.booted || !vm.dice) return;
    const selected = vm.selectedNumber;
    for (let i = 0; i < DIE_COUNT; i++) {
      const v = vm.dice[i] ?? 1;
      const match = v === selected;
      this.dice[i]?.setFace(v);
      this.dice[i]?.setHighlight(match ? 'match' : 'dim');
    }
  }

  // --- replay -------------------------------------------------------------

  /** True while a historical round is being re-presented (read-only). */
  isReplaying(): boolean {
    return this.replaying;
  }

  /**
   * Re-presents a COMPLETED round from stored history. It drives the exact same
   * animator with stored authoritative dice + match flags — it never calls the
   * network, never rolls, and never mutates live state. Resolves when the visual
   * replay finishes (or is cancelled). Call endReplay() afterwards to restore.
   */
  async playReplay(plan: ReplayPlan, reducedMotion: boolean): Promise<void> {
    if (!this.booted) return;
    this.setReplaying(true);
    // Reset to a clear PRE-ROLL state so a repeated replay visibly rolls again
    // instead of sitting on the previous final faces. Any in-flight animation is
    // cancelled first so rapid clicks never overlap loops.
    this.animator.cancel();
    this.resetDiceVisuals();
    // Show the historical pick (visual only — live selection is untouched).
    for (const c of this.cells) c.setSelected(c.value === plan.selectedNumber);
    await this.runRollAnimation(plan.dice, plan.matchFlags, plan.matchCount, plan.multiplier, reducedMotion);
  }

  /** Clears all three dice to a neutral pre-roll state (no highlight). */
  private resetDiceVisuals(): void {
    for (const d of this.dice) {
      d.setHighlight('none');
      d.setFace(1);
    }
  }

  /** Stops an in-flight replay animation immediately (snaps to final dice). */
  cancelReplay(): void {
    if (this.replaying) {
      this.animator.cancel();
      this.sound().stopAll();
    }
  }

  /** Exits replay mode and restores the live view from the current VM. */
  endReplay(vm: ViewModel): void {
    if (!this.replaying) return;
    this.replaying = false;
    // Force a full resync of the selection (the guard in update() compares to
    // this.selectedNumber, which replay left untouched).
    this.selectedNumber = vm.selectedNumber;
    for (const c of this.cells) c.setSelected(c.value === vm.selectedNumber);
    this.update(vm);
    this.showResult(vm);
  }

  private setReplaying(on: boolean): void {
    this.replaying = on;
    if (on) {
      this.btnRoll.setEnabled(false);
      this.btnBetDown.setEnabled(false);
      this.btnBetUp.setEnabled(false);
      this.btnMax.setEnabled(false);
      for (const c of this.cells) c.setEnabled(false);
    }
  }

  // --- responsive ---------------------------------------------------------

  relayout(viewport: Viewport, insets?: Insets): void {
    if (!this.booted || !this.parent) return;
    if (insets) this.insets = insets;
    const vp = viewport;
    const layout = computeSceneLayout(vp, this.insets);
    this.layout = layout;
    this.mode = layout.mode;
    this.parent.dataset.layoutKind = layout.kind;

    this.app.renderer.resize(vp.width, vp.height);
    this.background.layout(vp.width, vp.height);
    this.background.setReducedEffects(layout.reducedEffects);

    if (this.replayOnly) {
      this.layoutReplayOnly(vp);
      return;
    }

    const r = layout.regions;
    const toScreen = (rect: Rect): Rect =>
      layout.kind === 'unified'
        ? {
            x: layout.stage.x + rect.x * layout.stage.scale,
            y: layout.stage.y + rect.y * layout.stage.scale,
            w: rect.w * layout.stage.scale,
            h: rect.h * layout.stage.scale,
          }
        : rect;

    this.layoutTopbar(toScreen(r.topbar));
    this.layoutSelector(toScreen(r.selector));
    this.layoutDice(toScreen(r.dice));
    this.layoutStats(toScreen(r.stats), layout.kind);
    this.layoutControls(toScreen(r.controls), layout.kind);

    this.emitLayoutBounds();
  }

  private layoutTopbar(rect: Rect): void {
    // No wordmark: the whole top bar width is available to the right-aligned
    // utility toolbar (History / Rules / Sound). Sizing is pure math so the row
    // never overflows or overlaps, even on the narrowest phones.
    const btnH = Math.min(rect.h, 56);
    const slots = toolbarSlots(rect.w, btnH, 3, 10);
    const baseX = rect.x + rect.w - slots.totalW;
    const y = rect.y + (rect.h - slots.size) / 2;
    const buttons = [this.btnHistory, this.btnRules, this.btnSound];
    for (let i = 0; i < buttons.length; i++) {
      const b = buttons[i]!;
      b.resize(slots.size);
      b.position.set(baseX + i * (slots.size + slots.gap), y);
    }
  }

  private layoutSelector(rect: Rect): void {
    const gap = Math.max(8, rect.w * 0.02);
    const maxCellW = (rect.w - gap * 5) / 6;
    const size = Math.max(36, Math.min(rect.h, maxCellW));
    const totalW = size * 6 + gap * 5;
    let x = rect.x + (rect.w - totalW) / 2;
    const y = rect.y + (rect.h - size) / 2;
    for (const c of this.cells) {
      c.resize(size);
      c.position.set(x, y);
      x += size + gap;
    }
  }

  /**
   * Centered, controls-free dice layout for the standalone replay page. The
   * dice occupy a centered band in the UPPER part of the safe area; the lower
   * part is left clear for the DOM details/actions panel (positioned by CSS), so
   * the two never overlap. Works for portrait, landscape, and desktop because it
   * is derived purely from the actual screen safe rect.
   */
  private layoutReplayOnly(vp: Viewport): void {
    const ins = this.insets;
    const safe: Rect = {
      x: Math.max(0, ins.left),
      y: Math.max(0, ins.top),
      w: Math.max(1, vp.width - ins.left - ins.right),
      h: Math.max(1, vp.height - ins.top - ins.bottom),
    };
    const landscape = safe.w > safe.h;
    // Reserve the bottom for the details panel: less in landscape (panel is
    // shorter), more in portrait. Cap the dice band width for a clean desktop.
    const reserveBottom = landscape ? safe.h * 0.3 : safe.h * 0.4;
    const maxW = Math.min(safe.w, 900);
    const areaW = Math.max(160, maxW - 2 * Math.min(48, safe.w * 0.08));
    const areaH = Math.max(140, safe.h - reserveBottom);
    const gap = Math.max(8, areaH * 0.04);
    const resultH = Math.min(58, Math.max(44, areaH * 0.14));
    const diceVisualH = Math.max(100, areaH - resultH - gap);
    const dice: Rect = {
      x: safe.x + (safe.w - areaW) / 2,
      y: safe.y + Math.min(32, safe.h * 0.05),
      w: areaW,
      h: diceVisualH,
    };
    this.replayResultBlock = {
      x: dice.x,
      y: dice.y + diceVisualH + gap,
      w: dice.w,
      h: resultH,
    };
    this.layoutDice(dice);
    this.emitLayoutBounds();
  }

  private layoutDice(rect: Rect): void {
    const cluster = diceClusterLayout(rect, BASE_DIE);
    for (let i = 0; i < DIE_COUNT; i++) {
      const slot = this.dieSlots[i]!;
      slot.scale.set(cluster.slotScale);
      slot.position.set(cluster.centers[i]!, cluster.cy);
    }
  }

  private layoutStats(rect: Rect, kind: CompositionKind): void {
    // Placement math is pure + unit-tested (see statsLayout); the scene only
    // applies the resulting slots. Width-aware StatCard text fitting keeps long
    // money values inside each card.
    const slots = statsLayout(rect, kind);
    const cards = [this.cardBalance, this.cardBet, this.cardWin];
    for (let i = 0; i < cards.length; i++) {
      const s = slots[i]!;
      cards[i]!.resize(s.w, s.h, s.variant);
      cards[i]!.position.set(s.x, s.y);
    }
  }

  private layoutControls(rect: Rect, kind: CompositionKind): void {
    // Placement math is pure + unit-tested (see controlsLayout). On mobile this
    // is a 2-row layout (bet row + full-width Roll) so the Roll button can never
    // overflow the right edge as the old four-in-a-row did.
    const s = controlsLayout(rect, kind);
    this.btnBetDown.resize(s.betDown.w, s.betDown.h);
    this.btnBetDown.position.set(s.betDown.x, s.betDown.y);
    this.btnBetUp.resize(s.betUp.w, s.betUp.h);
    this.btnBetUp.position.set(s.betUp.x, s.betUp.y);
    this.btnMax.resize(s.max.w, s.max.h);
    this.btnMax.position.set(s.max.x, s.max.y);
    this.btnRoll.resize(s.roll.w, s.roll.h);
    this.btnRoll.position.set(s.roll.x, s.roll.y);
  }

  destroy(): void {
    this.animator?.dispose();
    this.app.destroy(true, { children: true });
  }
}
